import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  CircleQuestionMarkIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import uuid from "uuid";

import type { OpenAPI } from "simplydo/interfaces";
import { LabelItem } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { cx, styled } from "simplydo/react/styled";
import { mobileOnly } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  type DropdownOption,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Popup } from "simplydo/react/ui/popup";

import actions from "actions";
import api from "api";
import type { ChallengeIdeaFiltersState } from "reducers/challengeIdeaFilters";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import SlidingPane from "../SlidingModal";
import {
  applySavedIdeaFilters,
  clearPersistableIdeaFilters,
  countNonDefaultIdeaFilters,
  toPersistableIdeaFilters,
} from "./filterUtils";
import type {
  ChallengeIdeasResponse,
  ContentWrapperProps,
  FilterGroupOption,
  FilterParameterField,
  IdeaFilterUpdate,
  ProjectBoardLane,
} from "./types";

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  .filter-info {
    font-weight: bold;
    margin-bottom: 10px;
  }
  ${mobileOnly} {
    padding: 10px;
  }
`;

const RowFlex = styled.div`
  display: flex;
  flex-direction: column;
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PaneHeaderLink = styled.div`
  color: #2185d0;
  cursor: pointer;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  margin-top: 15px;
  margin-bottom: 3px;
  margin-left: 10px;
  font-weight: bold;
`;

const SavedFiltersRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
`;

const ListItem = styled.div`
  display: flex;
  flex: 1;
  max-width: 100%;
  padding: 5px 10px 3px 10px;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: #d2dde5;
  }
  &.is-selected {
    background: #d2dde5;
    &.is-clearable:hover {
      background: #c2cdd5;
    }
  }

  > .filter-option-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    max-width: 100%;

    > .filter-option {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      gap: 10px;

      > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 250px;
      }
    }
  }
`;

const definedOptions = <T,>(options: Array<T | undefined>): T[] =>
  options.filter((option): option is T => option != null);

const fieldChoicePrefix = (fieldId: string) => `${fieldId}:`;

// Issue with dropdown in popup content, therefore we need to render the popup content directly
const ContentWrapper = ({
  drawPopup,
  trigger,
  ...props
}: ContentWrapperProps) => {
  if (drawPopup) {
    return <Popup trigger={trigger} {...props} />;
  }
  return <>{trigger}</>;
};

type SelectableListOption = DropdownOption & {
  tooltip?: string;
  values?: string[];
  type?: string;
};

type SelectableListProps = {
  options: SelectableListOption[];
  selected?: string;
  onChange: (option: SelectableListOption) => void;
  clearable?: boolean;
  style?: React.CSSProperties;
  onOptionChange?: (options: string[]) => void;
  selectedValues?: string[];
};

const SelectableList = ({
  options,
  selected,
  onChange,
  clearable,
  style = {},
  onOptionChange,
  selectedValues = [],
}: SelectableListProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(options.length < 5 || clearable);

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: 2,
        ...style,
      }}
    >
      {(expanded ? options : options.slice(0, 5)).map((option) => (
        <ListItem
          key={option.value as string}
          className={cx(
            (clearable || option.value === selected) && "is-selected",
            clearable && "is-clearable",
          )}
          onClick={() => {
            onChange(option);
          }}
        >
          <ContentWrapper
            drawPopup={!option.values}
            disabled={
              !option.tooltip &&
              (typeof option.text !== "string" ||
                !option.text.length ||
                option.text.length < 25)
            }
            position="right center"
            offset={[-1, 10]}
            trigger={
              <div className="filter-option-wrapper">
                <div className="filter-option">
                  <span>{option.text}</span>
                  {clearable ? <Icon icon={XIcon} color="grey" /> : null}
                </div>
                {option.values ? (
                  <Dropdown
                    fluid
                    value={
                      selectedValues.find((c) =>
                        c.startsWith(fieldChoicePrefix(option.value as string)),
                      ) ?? ""
                    }
                    placeholder={t("ideas.filters.selectValue")}
                    onChange={(e, { value }) => {
                      const otherChoices = selectedValues.filter(
                        (choice) =>
                          !choice.startsWith(
                            fieldChoicePrefix(option.value as string),
                          ),
                      );
                      onOptionChange?.(
                        value
                          ? [...otherChoices, value as string]
                          : otherChoices,
                      );
                    }}
                    options={option.values.map((opt: string) => ({
                      key: `${option.value}:${opt}`,
                      value: `${option.value}:${opt}`,
                      text: opt,
                    }))}
                  />
                ) : null}
              </div>
            }
          >
            {option.tooltip ?? option.text}
          </ContentWrapper>
        </ListItem>
      ))}
      {!clearable && options.length > 5 ? (
        <ListItem
          onClick={() => setExpanded((prev) => !prev)}
          style={{ color: "#2185d0" }}
        >
          {expanded ? t("ideas.filters.showLess") : t("ideas.filters.showMore")}
        </ListItem>
      ) : null}
    </div>
  );
};

type ChallengeIdeasResponseAlias = ChallengeIdeasResponse;

type ChallengeIdeaFiltersProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  popularTags?: OpenAPI.Schemas["Tag"][];
  allLabels?: string[];
  availableGroups?: ChallengeIdeasResponseAlias["availableGroups"];
  availableOrganisations?: ChallengeIdeasResponseAlias["availableOrganisations"];
  availableSortParameters: ChallengeIdeasResponseAlias["availableSortParameters"];
  availableFilterParameters: ChallengeIdeasResponseAlias["availableFilterParameters"];
  availableImpacts?: ChallengeIdeasResponseAlias["availableImpacts"];
  allProjectAssignees?: OpenAPI.Schemas["User"][];
};

function ChallengeIdeaFilters({
  challenge,
  popularTags = [],
  allLabels = [],
  availableGroups = [],
  availableOrganisations = [],
  availableSortParameters = {},
  availableFilterParameters = {},
  availableImpacts = [],
  allProjectAssignees = [],
}: ChallengeIdeaFiltersProps) {
  const dispatch = useAppDispatch();
  const ideaFilters = useAppSelector((state) => state.challengeIdeaFilters);
  const { filtersVisible, ideaViewType } = ideaFilters;
  const user = useAppSelector((state) => state.user);
  const numberOfFiltersNotDefault = countNonDefaultIdeaFilters(ideaFilters);
  const updateFilters = useCallback(
    (data: IdeaFilterUpdate) =>
      dispatch(actions.challengeIdeaFilters.update(data)),
    [dispatch],
  );
  const clearAllFilters = useCallback(() => {
    updateFilters(clearPersistableIdeaFilters());
  }, [updateFilters]);
  const { t } = useTranslation();
  const [savedFilterName, setSavedFilterName] = useState("");
  const [savedFilterSearch, setSavedFilterSearch] = useState("");

  const savedPresets = user?.savedChallengeIdeaFilters;
  const filteredSavedPresets = useMemo(() => {
    const presets = savedPresets ?? [];
    const query = savedFilterSearch.trim().toLowerCase();
    if (!query) {
      return presets;
    }
    return presets.filter((preset) =>
      preset.name.toLowerCase().includes(query),
    );
  }, [savedPresets, savedFilterSearch]);

  const persistSavedFilters = useCallback(
    (
      next: NonNullable<OpenAPI.Schemas["User"]["savedChallengeIdeaFilters"]>,
    ) => {
      if (!user?._id) return;
      const previous = user.savedChallengeIdeaFilters ?? [];
      dispatch(actions.user.updateSavedChallengeIdeaFilters(next));
      api.users.updateChallengeIdeaFilters(
        user._id,
        { savedChallengeIdeaFilters: next },
        () => {},
        () => {
          dispatch(actions.user.updateSavedChallengeIdeaFilters(previous));
        },
      );
    },
    [dispatch, user],
  );

  const saveCurrentFilters = useCallback(() => {
    const name = savedFilterName.trim();
    if (!name || !user?._id) return;
    const next = [
      ...(user.savedChallengeIdeaFilters ?? []),
      { id: uuid.v4(), name, filters: toPersistableIdeaFilters(ideaFilters) },
    ];
    persistSavedFilters(next);
    setSavedFilterName("");
  }, [savedFilterName, user, ideaFilters, persistSavedFilters]);

  const deleteSavedFilter = useCallback(
    (id: string, name: string) => {
      util
        .confirm(
          t("ideas.filters.saved.deleteConfirm.title"),
          t("ideas.filters.saved.deleteConfirm.info", { name }),
        )
        .then(() => {
          persistSavedFilters(
            (user?.savedChallengeIdeaFilters ?? []).filter(
              (preset) => preset.id !== id,
            ),
          );
        })
        .catch(() => {});
    },
    [persistSavedFilters, user, t],
  );

  const applySavedFilter = useCallback(
    (
      filters: NonNullable<
        OpenAPI.Schemas["User"]["savedChallengeIdeaFilters"]
      >[number]["filters"],
    ) => {
      updateFilters(
        applySavedIdeaFilters(
          filters as Parameters<typeof applySavedIdeaFilters>[0],
        ),
      );
    },
    [updateFilters],
  );

  const sortOptionsConfig = [
    { type: "default", label: "generic.default" },
    { type: "name", label: "order.name" },
    { type: "voteCount", label: "order.votes", showIfVotingEnabled: true },
    { type: "completeness", label: "order.completeness" },
    { type: "updatedAt", label: "order.updatedAt" },
    { type: "createdAt", label: "order.createdAt" },
    { type: "submittedAt", label: "order.submittedAt", challengeManager: true },
    {
      type: "assessmentScore",
      label: "order.assessmentScore",
      challengeManager: true,
    },
    { type: "score", label: "order.smartScore", challengeManager: true },
  ];

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );

  const challengeId = challenge?._id;
  const defaultLaneName = challenge?.projectBoard?.defaultLane?.name;
  const [allChallengeProjectLanes, setAllChallengeProjectLanes] = useState<
    ProjectBoardLane[]
  >([]);
  const getChallengeProjectLanes = useCallback(() => {
    api.boards.getProjectLanes(
      challengeId,
      (data) => {
        setAllChallengeProjectLanes([
          { _id: "default", name: defaultLaneName ?? "Default" },
          ...data.lanes,
        ]);
      },
      () => {},
    );
  }, [challengeId, defaultLaneName]);
  useEffect(() => {
    if (filtersVisible) {
      getChallengeProjectLanes();
    }
  }, [getChallengeProjectLanes, filtersVisible]);

  // Fields added in the UI before a value is chosen (not sent to the API).
  const [pendingFieldIds, setPendingFieldIds] = useState<string[]>([]);

  const selectedFilterParameters = useMemo(() => {
    const fromChoices = ideaFilters.fieldChoices
      .map((choice) => {
        const [fieldId, fieldVal] = choice.split(":", 2);
        return fieldId && fieldVal ? fieldId : null;
      })
      .filter((id): id is string => Boolean(id));
    return [...new Set([...fromChoices, ...pendingFieldIds])];
  }, [ideaFilters.fieldChoices, pendingFieldIds]);

  useEffect(() => {
    const chosenFieldIds = new Set(
      ideaFilters.fieldChoices
        .map((choice) => {
          const [fieldId, fieldVal] = choice.split(":", 2);
          return fieldId && fieldVal ? fieldId : null;
        })
        .filter((id): id is string => Boolean(id)),
    );
    setPendingFieldIds((prev) => prev.filter((id) => !chosenFieldIds.has(id)));
  }, [ideaFilters.fieldChoices]);

  const [usedFilterParameters, unusedFilterParameters] = useMemo(() => {
    const nextUsedFilterParameters: FilterParameterField[] = [];
    const nextUnusedFilterParameters: FilterParameterField[] = [];

    availableFilterParameters?.fields?.forEach((param) => {
      if (selectedFilterParameters.includes(param._id ?? "")) {
        nextUsedFilterParameters.push(param as FilterParameterField);
      } else {
        nextUnusedFilterParameters.push(param as FilterParameterField);
      }
    });

    return [nextUsedFilterParameters, nextUnusedFilterParameters];
  }, [selectedFilterParameters, availableFilterParameters]);

  const extraSortOptions = useMemo(() => {
    const { sliders = [] } = availableSortParameters;

    return sliders.map((param) => ({
      key: `${param.value}`,
      value: `${param.value}`,
      text: t("ideas.filters.sliderSort", { name: param.name }),
    }));
  }, [availableSortParameters, t]);

  const filterProjectAssignment = useMemo(
    () => [
      { key: "me", value: "me", text: t("ideas.filters.assignees.me") },
      {
        key: "unassigned",
        value: "unassigned",
        text: t("ideas.filters.assignees.unassigned"),
      },
      ...allProjectAssignees.map((assignee) => ({
        key: assignee._id,
        value: assignee._id,
        text: t("ideas.filters.assignees.user", {
          name: assignee.profile.fullName,
        }),
      })),
    ],
    [allProjectAssignees, t],
  );

  if (
    user &&
    (challenge?.ideaVisibility === "users" ||
      canManageChallenge ||
      util.hasPermission(user, "challenge.viewIdeas", challenge?._id))
  ) {
    const sortOptions = [
      ...sortOptionsConfig
        .filter(
          (f) =>
            !(
              f.challengeManager &&
              !(
                canManageChallenge ||
                util.hasPermission(user, "challenge.viewIdeas", challenge?._id)
              )
            ),
        )
        .filter(
          (f) =>
            !(
              f.showIfVotingEnabled &&
              !canManageChallenge &&
              !util.hasPermission(
                user,
                "challenge.viewIdeas",
                challenge?._id,
              ) &&
              challenge?.voteCountVisibility !== "users"
            ),
        )
        .map((f) => ({ key: f.type, text: t(f.label), value: f.type })),
      ...extraSortOptions,
    ];

    const filterTags = popularTags.map((pTag) => ({
      key: pTag._id,
      value: pTag._id,
      text: pTag.value,
    }));

    const filterGroups: FilterGroupOption[] = availableGroups.map((group) => ({
      key: group._id ?? "",
      text: group.name,
      value: group._id,
      type: "group",
    }));

    const filterOrganisations = availableOrganisations.map((organisation) => ({
      key: organisation._id,
      text: organisation.name,
      value: organisation._id,
      type: "organisation",
    }));

    const filterLabels = allLabels.map((label) => ({
      key: label,
      value: label,
      text: <LabelItem label={label} />,
    }));

    const filterAuthors: FilterGroupOption[] =
      filterOrganisations.length > 1
        ? filterGroups.concat([...filterOrganisations] as FilterGroupOption[])
        : filterGroups;

    const impactOptions = availableImpacts.map((impact) => ({
      key: impact._id,
      value: impact._id,
      text: impact.name,
    }));

    const canViewStatus =
      canManageChallenge ||
      (challenge.ideaVisibility === "users" &&
        !challenge.ideaVisibilityLimits) ||
      util.hasPermission(user, "challenge.viewIdeas", challenge?._id) ||
      util.hasPermission(user, "challenge.viewProjectBoard", challenge?._id);

    const canViewProjectFilters =
      canManageChallenge ||
      util.hasPermission(user, "challenge.viewProjectBoard", challenge?._id) ||
      challenge?.projectManagementVisibility === "users";

    return (
      <SlidingPane
        isOpen={filtersVisible}
        from="left"
        width={400}
        onClose={() => updateFilters({ filtersVisible: false })}
      >
        <div style={{ padding: 16, flex: 1 }}>
          <PaneHeader>
            <PaneHeaderLink
              onClick={() => {
                updateFilters({ filtersVisible: false });
              }}
            >
              {t("ideas.filters.hide")}
            </PaneHeaderLink>
            {numberOfFiltersNotDefault > 0 ? (
              <PaneHeaderLink onClick={clearAllFilters}>
                {t("ideas.filters.clear")}
              </PaneHeaderLink>
            ) : null}
          </PaneHeader>
          <SectionHeader>{t("ideas.filters.saved.title")}</SectionHeader>

          {savedPresets?.length ? (
            <Dropdown
              fluid
              search
              variant="outline"
              placeholder={t("ideas.filters.saved.searchPlaceholder")}
              searchQuery={savedFilterSearch}
              onSearchChange={(_e, { searchQuery }) =>
                setSavedFilterSearch(searchQuery)
              }
              style={{ marginTop: 8 }}
            >
              <DropdownMenu>
                {filteredSavedPresets.length === 0 ? (
                  <DropdownItem disabled>
                    {t("ideas.filters.saved.noResults")}
                  </DropdownItem>
                ) : (
                  filteredSavedPresets.map((preset) => (
                    <Dropdown
                      key={preset.id}
                      trigger={<DropdownItem>{preset.name}</DropdownItem>}
                    >
                      <DropdownMenu>
                        <DropdownItem
                          onClick={() => applySavedFilter(preset.filters)}
                        >
                          <Icon icon={CheckIcon} />
                          {t("ideas.filters.saved.apply")}
                        </DropdownItem>
                        <DropdownItem
                          onClick={() =>
                            deleteSavedFilter(preset.id, preset.name)
                          }
                        >
                          <Icon icon={TrashIcon} />
                          {t("ideas.filters.saved.delete")}
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  ))
                )}
              </DropdownMenu>
            </Dropdown>
          ) : null}
          <SavedFiltersRow>
            <Input
              fluid
              size="small"
              placeholder={t("ideas.filters.saved.namePlaceholder")}
              value={savedFilterName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSavedFilterName(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveCurrentFilters();
                }
              }}
            />
            <Button
              size="small"
              color="primary"
              disabled={!savedFilterName.trim()}
              onClick={saveCurrentFilters}
            >
              {t("ideas.filters.saved.save")}
            </Button>
          </SavedFiltersRow>
          <div>
            <OptionsContainer>
              <RowFlex>
                {ideaViewType !== "board" ? (
                  <>
                    <SectionHeader>{t("ideas.filters.sort")}</SectionHeader>
                    <Dropdown
                      fluid
                      search
                      variant="outline"
                      clearable={false}
                      value={ideaFilters.sort}
                      options={sortOptions}
                      onChange={(e, { value }) =>
                        updateFilters({
                          sort: value as ChallengeIdeaFiltersState["sort"],
                        })
                      }
                    />
                    <Dropdown
                      fluid
                      variant="outline"
                      clearable={false}
                      value={ideaFilters.sortDirection}
                      style={{ marginTop: 5 }}
                      options={[
                        {
                          key: "asc",
                          value: "asc",
                          text: t("ideas.filters.sortAsc"),
                        },
                        {
                          key: "desc",
                          value: "desc",
                          text: t("ideas.filters.sortDesc"),
                        },
                      ]}
                      onChange={(e, { value }) =>
                        updateFilters({
                          sortDirection:
                            value as ChallengeIdeaFiltersState["sortDirection"],
                        })
                      }
                    />
                  </>
                ) : null}

                {canViewStatus ? (
                  <>
                    <SectionHeader>{t("ideas.filters.status")}</SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      clearable={false}
                      value={ideaFilters.status || "all"}
                      options={[
                        {
                          key: "all",
                          value: "all",
                          text: t("ideas.filters.all"),
                        },
                        {
                          key: "draft",
                          value: "draft",
                          text: t("ideas.filters.draft"),
                        },
                        {
                          key: "submitted",
                          value: "submitted",
                          text: t("ideas.filters.submitted"),
                        },
                      ]}
                      onChange={(e, { value }) =>
                        updateFilters({
                          status:
                            (value as ChallengeIdeaFiltersState["status"]) ||
                            "all",
                        })
                      }
                    />
                  </>
                ) : null}

                {availableImpacts.length ? (
                  <>
                    <SectionHeader>
                      {t("common:capitalise", { key: "generic.impact" })}
                    </SectionHeader>
                    <Dropdown
                      fluid
                      search
                      variant="outline"
                      value={[]}
                      placeholder={t("ideas.filters.searchImpacts", {
                        impacts: t("generic.impacts"),
                      })}
                      onChange={(e, { value }) => {
                        if (
                          !value ||
                          ideaFilters.impactIds.includes(value as string)
                        ) {
                          return;
                        }
                        updateFilters({
                          impactIds: [
                            ...ideaFilters.impactIds,
                            value as string,
                          ],
                        });
                      }}
                      options={impactOptions.filter(
                        (impact) => !ideaFilters.impactIds.includes(impact.key),
                      )}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={impactOptions.filter((impact) =>
                        ideaFilters.impactIds.includes(impact.key),
                      )}
                      onChange={({ value }) => {
                        updateFilters({
                          impactIds: ideaFilters.impactIds.filter(
                            (impact) => impact !== value,
                          ),
                        });
                      }}
                    />
                  </>
                ) : null}

                {popularTags?.length ? (
                  <>
                    <SectionHeader>{t("ideas.filters.tags")}</SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      search
                      value={[]}
                      placeholder={t("ideas.filters.searchTags")}
                      onChange={(e, { value }) => {
                        if (
                          !value ||
                          ideaFilters.tags.includes(value as string)
                        )
                          return;
                        updateFilters({
                          tags: [...ideaFilters.tags, value as string],
                        });
                      }}
                      options={filterTags.filter(
                        (tag) => !ideaFilters.tags.includes(tag.value),
                      )}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={definedOptions(
                        ideaFilters.tags.map((tag) =>
                          filterTags.find((pTag) => pTag.value === tag),
                        ),
                      )}
                      onChange={({ value }) =>
                        updateFilters({
                          tags: ideaFilters.tags.filter((tag) => tag !== value),
                        })
                      }
                    />
                  </>
                ) : null}
                {(availableGroups?.length > 1 ||
                  availableOrganisations?.length > 1) &&
                (canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.viewIdeas",
                    challenge?._id,
                  )) ? (
                  <>
                    <SectionHeader>{t("groups.authorGroups")}</SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      value={[]}
                      search
                      placeholder={t("groups.search")}
                      onChange={(e, { value }) => {
                        if (!value) return;
                        const option = filterAuthors.find(
                          (author) => author.value === value,
                        );
                        if (!option?.value) return;
                        if (option.type === "group") {
                          if (ideaFilters.authorGroupIds.includes(option.value))
                            return;
                          updateFilters({
                            authorGroupIds: [
                              ...ideaFilters.authorGroupIds,
                              option.value,
                            ],
                          });
                        } else {
                          if (
                            ideaFilters.authorOrganisationIds.includes(
                              option.value,
                            )
                          ) {
                            return;
                          }
                          updateFilters({
                            authorOrganisationIds: [
                              ...ideaFilters.authorOrganisationIds,
                              option.value,
                            ],
                          });
                        }
                      }}
                      options={
                        filterAuthors.filter(
                          (author) =>
                            !(
                              author.value &&
                              ideaFilters.authorGroupIds.includes(author.value)
                            ) &&
                            !(
                              author.value &&
                              ideaFilters.authorOrganisationIds.includes(
                                author.value,
                              )
                            ),
                        ) as (DropdownOption & { key: string })[]
                      }
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={definedOptions(
                        ideaFilters.authorGroupIds
                          .concat(ideaFilters.authorOrganisationIds)
                          .map((a) =>
                            filterAuthors.find((pTag) => pTag.value === a),
                          ),
                      )}
                      onChange={({ value, type }) =>
                        updateFilters(
                          type === "group"
                            ? {
                                authorGroupIds:
                                  ideaFilters.authorGroupIds.filter(
                                    (g) => g !== value,
                                  ),
                              }
                            : {
                                authorOrganisationIds:
                                  ideaFilters.authorOrganisationIds.filter(
                                    (o) => o !== value,
                                  ),
                              },
                        )
                      }
                    />
                  </>
                ) : null}

                {allLabels.length ? (
                  <>
                    <SectionHeader>{t("ideas.filters.labels")}</SectionHeader>
                    <Dropdown
                      fluid
                      value={[]}
                      variant="outline"
                      placeholder={t("ideas.filters.searchLabels")}
                      onChange={(e, { value }) => {
                        if (
                          !value ||
                          ideaFilters.labels.includes(value as string)
                        )
                          return;
                        updateFilters({
                          labels: [...ideaFilters.labels, value as string],
                        });
                      }}
                      options={filterLabels.filter(
                        (label) => !ideaFilters.labels.includes(label.value),
                      )}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={definedOptions(
                        ideaFilters.labels.map((tag) =>
                          filterLabels.find((pTag) => pTag.value === tag),
                        ),
                      )}
                      onChange={({ value }) =>
                        updateFilters({
                          labels: ideaFilters.labels.filter(
                            (tag) => tag !== value,
                          ),
                        })
                      }
                    />
                  </>
                ) : null}

                {canViewProjectFilters ? (
                  <>
                    <SectionHeader>
                      {t("ideas.filters.projectAssignment")}
                    </SectionHeader>
                    <Dropdown
                      fluid
                      value={[]}
                      search
                      variant="outline"
                      placeholder={t("ideas.filters.searchAssignee")}
                      options={filterProjectAssignment.filter(
                        (assignee) =>
                          !ideaFilters.assignees.includes(
                            assignee.value as (typeof ideaFilters.assignees)[number],
                          ),
                      )}
                      onChange={(e, { value }) => {
                        if (!value) return;
                        const option = filterProjectAssignment.find(
                          (assignee) => assignee.value === value,
                        );
                        if (
                          !option ||
                          ideaFilters.assignees.includes(
                            option.value as (typeof ideaFilters.assignees)[number],
                          )
                        ) {
                          return;
                        }
                        updateFilters({
                          assignees: [
                            ...ideaFilters.assignees,
                            option.value as (typeof ideaFilters.assignees)[number],
                          ],
                        });
                      }}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={filterProjectAssignment.filter((assignee) =>
                        ideaFilters.assignees.includes(
                          assignee.value as (typeof ideaFilters.assignees)[number],
                        ),
                      )}
                      onChange={({ value }) =>
                        updateFilters({
                          assignees: ideaFilters.assignees.filter(
                            (assignee) => assignee !== value,
                          ),
                        })
                      }
                    />

                    <SectionHeader>
                      {t("ideas.filters.projectLane")}
                    </SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      value={[]}
                      search
                      placeholder={t("ideas.filters.searchLane")}
                      onChange={(e, { value }) => {
                        if (
                          !value ||
                          ideaFilters.lanes.includes(value as string)
                        )
                          return;
                        updateFilters({
                          lanes: [...ideaFilters.lanes, value as string],
                        });
                      }}
                      options={allChallengeProjectLanes
                        .filter((lane) => !ideaFilters.lanes.includes(lane._id))
                        .map((lane) => ({
                          key: lane._id,
                          value: lane._id,
                          text: lane.name,
                        }))}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={allChallengeProjectLanes
                        .filter((lane) => ideaFilters.lanes.includes(lane._id))
                        .map((lane) => ({
                          key: lane._id,
                          value: lane._id,
                          text: lane.name,
                        }))}
                      onChange={({ value }) =>
                        updateFilters({
                          lanes: ideaFilters.lanes.filter(
                            (lane) => lane !== value,
                          ),
                        })
                      }
                    />
                  </>
                ) : null}

                {canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.editIdeaDeadlines",
                  challenge?._id,
                ) ||
                util.hasPermission(
                  user,
                  "challenge.viewIdeas",
                  challenge?._id,
                ) ? (
                  <>
                    <SectionHeader>
                      {t("ideas.filters.customDeadline")}
                    </SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      clearable={false}
                      value={ideaFilters.hasCustomDeadline || "all"}
                      options={[
                        {
                          key: "all",
                          value: "all",
                          text: t("ideas.filters.all"),
                        },
                        {
                          key: "yes",
                          value: "yes",
                          text: t("ideas.filters.customDeadline.with"),
                        },
                        {
                          key: "no",
                          value: "no",
                          text: t("ideas.filters.customDeadline.without"),
                        },
                      ]}
                      onChange={(e, { value }) =>
                        updateFilters({
                          hasCustomDeadline:
                            value === "yes" || value === "no" ? value : "all",
                        })
                      }
                    />
                  </>
                ) : null}

                {util.canManageChallenge(user, challenge) ||
                util.hasPermission(
                  user,
                  "challenge.manageIdeaComments",
                  challenge?._id,
                ) ? (
                  <>
                    <SectionHeader>
                      {t("ideas.filters.adminComments")}
                    </SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      clearable={false}
                      value={ideaFilters.hasAdminComment || "all"}
                      options={[
                        {
                          key: "all",
                          value: "all",
                          text: t("ideas.filters.all"),
                        },
                        {
                          key: "yes",
                          value: "yes",
                          text: t("ideas.filters.adminComments.with"),
                        },
                        {
                          key: "no",
                          value: "no",
                          text: t("ideas.filters.adminComments.without"),
                        },
                      ]}
                      onChange={(e, { value }) =>
                        updateFilters({
                          hasAdminComment:
                            value === "yes" || value === "no" ? value : "all",
                        })
                      }
                    />
                  </>
                ) : null}

                {unusedFilterParameters.length ||
                usedFilterParameters.length ? (
                  <>
                    <Popup
                      trigger={
                        <SectionHeader>
                          {t("ideas.filters.content")}{" "}
                          <Icon icon={CircleQuestionMarkIcon} size="small" />
                        </SectionHeader>
                      }
                      position="right center"
                      offset={[-1, 10]}
                    >
                      {t("ideas.filters.content.tooltip", {
                        ideas: t("generic.ideas"),
                      })}
                    </Popup>
                    <Dropdown
                      fluid
                      variant="outline"
                      value={[]}
                      style={{ marginBottom: 5 }}
                      placeholder={t("ideas.filters.addFilter")}
                      onChange={(e, { value }) => {
                        if (
                          !value ||
                          selectedFilterParameters.includes(value as string)
                        ) {
                          return;
                        }
                        setPendingFieldIds((prev) => [
                          ...prev,
                          value as string,
                        ]);
                      }}
                      options={unusedFilterParameters.map((param) => ({
                        key: param._id,
                        value: param._id,
                        text: param.name,
                      }))}
                    />
                    <SelectableList
                      clearable
                      options={usedFilterParameters.map((param) => ({
                        key: param._id,
                        value: param._id,
                        text: param.name,
                        values: param.options,
                      }))}
                      onChange={({ value }) => {
                        setPendingFieldIds((prev) =>
                          prev.filter((id) => id !== value),
                        );
                        updateFilters({
                          fieldChoices: ideaFilters.fieldChoices.filter(
                            (choice) =>
                              !choice.startsWith(
                                fieldChoicePrefix(String(value)),
                              ),
                          ),
                        });
                      }}
                      onOptionChange={(optionValues) =>
                        updateFilters({ fieldChoices: optionValues })
                      }
                      selectedValues={ideaFilters.fieldChoices}
                    />
                  </>
                ) : null}
              </RowFlex>
            </OptionsContainer>
          </div>
          <div style={{ height: 32 }}></div>
        </div>
      </SlidingPane>
    );
  }
  return null;
}

export default ChallengeIdeaFilters;
